var Record_1;
import { __decorate } from "tslib";
import { define, definitions, isProduction, logger, mixinRules, tools } from '../object-plus/index.js';
import { Transactional } from '../transactions.js';
import { type } from './attrDef.js';
import { IORecordMixin } from './io-mixin.js';
import { AggregatedType, AnyType } from './metatypes/index.js';
import { setAttribute, shouldBeAnObject, unknownAttrsWarning, UpdateRecordMixin } from './updates.js';
const { assign, isEmpty } = tools;
let _cidCounter = 0;
let Record = Record_1 = class Record extends Transactional {
    static onDefine(definition, BaseClass) { }
    static get ref() {
        return type(this)
            .toJSON(x => x ? x.id : null)
            .parse(x => {
            return { [this.prototype.idAttribute]: x };
        });
    }
    static extendAttrs(attrs) {
        return this.defaults(attrs);
    }
    static defaults(attrs) {
        return this.extend({ attributes: attrs });
    }
    previousAttributes() { return new this.AttributesCopy(this._previousAttributes); }
    get __inner_state__() { return this.attributes; }
    get changed() {
        let changed = this._changedAttributes;
        if (!changed) {
            const prev = this._previousAttributes;
            changed = {};
            const { attributes } = this;
            for (let attr of this._attributesArray) {
                const key = attr.name, value = attributes[key];
                if (attr.isChanged(value, prev[key])) {
                    changed[key] = value;
                }
            }
            this._changedAttributes = changed;
        }
        return changed;
    }
    changedAttributes(diff) {
        if (!diff)
            return this.hasChanged() ? Object.assign({}, this.changed) : false;
        var val, changed = false, old = this._transaction ? this._previousAttributes : this.attributes, attrSpecs = this._attributes;
        for (var attr in diff) {
            if (!attrSpecs[attr].isChanged(old[attr], (val = diff[attr])))
                continue;
            (changed || (changed = {}))[attr] = val;
        }
        return changed;
    }
    hasChanged(key) {
        const { _previousAttributes } = this;
        if (!_previousAttributes)
            return false;
        return key ?
            this._attributes[key].isChanged(this.attributes[key], _previousAttributes[key]) :
            !isEmpty(this.changed);
    }
    previous(key) {
        if (key) {
            const { _previousAttributes } = this;
            if (_previousAttributes)
                return _previousAttributes[key];
        }
        return null;
    }
    isNew() {
        return this.id == null;
    }
    has(key) {
        return this[key] != void 0;
    }
    unset(key, options) {
        const value = this[key];
        this.set({ [key]: void 0 }, Object.assign({ unset: true }, options));
        return value;
    }
    clear(options) {
        const nullify = options && options.nullify;
        this.transaction(() => {
            this.forEach((value, key) => this[key] = nullify ? null : void 0);
        }, options);
        return this;
    }
    getOwner() {
        const owner = this._owner;
        return this._ownerKey ? owner : owner && owner._owner;
    }
    get id() { return this.attributes[this.idAttribute]; }
    set id(x) { setAttribute(this, this.idAttribute, x); }
    defaults(values = {}) {
        const defaults = {}, { _attributesArray } = this;
        for (let attr of _attributesArray) {
            const key = attr.name, value = values[key];
            defaults[key] = value === void 0 ? attr.defaultValue() : value;
        }
        return defaults;
    }
    constructor(a_values, a_options) {
        super(_cidCounter++);
        this.attributes = {};
        const options = a_options || {}, values = (options.parse ? this.parse(a_values, options) : a_values) || {};
        isProduction || typeCheck(this, values, options);
        this._previousAttributes = this.attributes = new this.Attributes(this, values, options);
        this.initialize(a_values, a_options);
        if (this._localEvents)
            this._localEvents.subscribe(this, this);
    }
    initialize(values, options) { }
    clone(options = {}) {
        const copy = new this.constructor(this.attributes, { clone: true });
        if (options.pinStore)
            copy._defaultStore = this.getStore();
        return copy;
    }
    _validateNested(errors) {
        var length = 0;
        const { attributes } = this;
        for (let attribute of this._attributesArray) {
            const { name } = attribute, error = attribute.validate(this, attributes[name], name);
            if (error) {
                errors[name] = error;
                length++;
            }
        }
        return length;
    }
    get(key) {
        return this[key];
    }
    set(values, options) {
        if (values) {
            const transaction = this._createTransaction(values, options);
            transaction && transaction.commit();
        }
        return this;
    }
    toJSON(options) {
        const json = {}, { attributes } = this;
        for (let attribute of this._attributesArray) {
            const { name } = attribute, value = attributes[name];
            if (value !== void 0) {
                const asJson = attribute.toJSON.call(this, value, name, options);
                if (asJson !== void 0)
                    json[name] = asJson;
            }
        }
        return json;
    }
    parse(data, options) {
        return data;
    }
    deepSet(name, value, options) {
        this.transaction(() => {
            const path = name.split('.'), l = path.length - 1, attr = path[l];
            let model = this;
            for (let i = 0; i < l; i++) {
                const key = path[i];
                let next = model.get ? model.get(key) : model[key];
                if (!next) {
                    const attrSpecs = model._attributes;
                    if (attrSpecs) {
                        var newModel = attrSpecs[key].create();
                        if (options && options.nullify && newModel._attributes) {
                            newModel.clear(options);
                        }
                        model[key] = next = newModel;
                    }
                    else
                        return;
                }
                model = next;
            }
            if (model.set) {
                model.set({ [attr]: value }, options);
            }
            else {
                model[attr] = value;
            }
        });
        return this;
    }
    get collection() {
        return this._ownerKey ? null : this._owner;
    }
    dispose() {
        if (this._disposed)
            return;
        const { attributes } = this;
        for (let attr of this._attributesArray) {
            attr.dispose(this, attributes[attr.name]);
        }
        super.dispose();
    }
    _log(level, topic, text, props, a_logger) {
        (a_logger || logger).trigger(level, topic, this.getClassName() + ' ' + text, Object.assign(Object.assign({}, props), { 'Record': this, 'Attributes definition': this._attributes }));
    }
    getClassName() {
        return super.getClassName() || 'Record';
    }
    _createTransaction(values, options) { return void 0; }
    forEach(iteratee, context) {
        const fun = context !== void 0 ? (v, k) => iteratee.call(context, v, k) : iteratee, { attributes } = this;
        for (const key in this.attributes) {
            const value = attributes[key];
            if (value !== void 0)
                fun(value, key);
        }
    }
    mapObject(a_fun, context) {
        const fun = context === void 0 ? a_fun : a_fun.bind(context);
        return tools.transform({}, this.attributes, fun);
    }
    [Symbol.iterator]() {
        return new RecordEntriesIterator(this);
    }
    entries() {
        return new RecordEntriesIterator(this);
    }
    keys() {
        const keys = [];
        this.forEach((value, key) => keys.push(key));
        return keys;
    }
};
Record._metatype = AggregatedType;
Record.id = type(String).value(null);
Record = Record_1 = __decorate([
    define({
        cidPrefix: 'm',
        _changeEventName: 'change',
        idAttribute: 'id'
    }),
    definitions({
        defaults: mixinRules.merge,
        attributes: mixinRules.merge,
        collection: mixinRules.merge,
        Collection: mixinRules.value,
        idAttribute: mixinRules.protoValue
    })
], Record);
export { Record };
;
assign(Record.prototype, UpdateRecordMixin, IORecordMixin);
class BaseRecordAttributes {
    constructor(record, x, options) {
        this.id = x.id;
    }
}
Record.prototype.Attributes = BaseRecordAttributes;
class BaseRecordAttributesCopy {
    constructor(x) {
        this.id = x.id;
    }
}
Record.prototype.AttributesCopy = BaseRecordAttributesCopy;
const IdAttribute = AnyType.create({ value: void 0 }, 'id');
Record.prototype._attributes = { id: IdAttribute };
Record.prototype._attributesArray = [IdAttribute];
function typeCheck(record, values, options) {
    if (shouldBeAnObject(record, values, options)) {
        const { _attributes } = record;
        let unknown;
        for (let name in values) {
            if (!_attributes[name]) {
                unknown || (unknown = []);
                unknown.push(`'${name}'`);
            }
        }
        if (unknown) {
            unknownAttrsWarning(record, unknown, { values }, options);
        }
    }
}
export class RecordEntriesIterator {
    constructor(record) {
        this.record = record;
        this.idx = 0;
    }
    next() {
        const { record } = this, metatype = record._attributesArray[this.idx++];
        return {
            done: !metatype,
            value: metatype ? [metatype.name, record[metatype.name]] : void 0
        };
    }
}
//# sourceMappingURL=record.js.map